import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bluetooth, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '@/components/common/PageContainer'
import { AppButton } from '@/components/rfid/AppButton'
import { EngineListSearch, engineSearchMatch } from '@/components/rfid/EngineListSearch'
import { ManualTagEntry } from '@/components/rfid/ManualTagEntry'
import { MaterialSkuSelect } from '@/components/rfid/MaterialSkuSelect'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { ScanCaptureHint } from '@/components/scanner/ScanCaptureHint'
import { BRAND, SCAN_HERO_SOFT } from '@/constants/brand'
import { useTagSkuScanSubscription } from '@/hooks/useTagSkuScanSubscription'
import { useLocale } from '@/i18n/LocaleProvider'
import {
  ensureMockConnected,
  isMockScannerEnabled,
} from '@/services/scannerService'
import { engineService, type EngineItemError, type TagResult } from '@/services/engine.service'
import { rfidService } from '@/services/rfid.service'
import { useTagSkuScanStore } from '@/stores/tagSkuScanStore'
import { isScannerSessionActive, useRfidSessionStore } from '@/stores/rfidSessionStore'
import { materialMasterDropdownOptions } from '@/utils/materialMaster'
import { cn } from '@/utils/cn'
import {
  engineItemErrorLabel,
  singleEngineErrorMessage,
  summarizeEngineBatch,
  type EngineApiError,
  type EngineErrorLabels,
} from '@/utils/engineErrors'

type Row = { tagId: string; hits: number; sku: string }

export default function EngineTagPage() {
  const navigate = useNavigate()
  const { t } = useLocale()

  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerPending = useRfidSessionStore((s) => s.scannerPending)
  const deviceConnected = useRfidSessionStore((s) => s.deviceConnected)
  const scannerType = useRfidSessionStore((s) => s.scannerType)

  const rawScans = useTagSkuScanStore((s) => s.rawScans)
  const uniqueTags = useTagSkuScanStore((s) => s.uniqueTags)
  const tagHitCount = useTagSkuScanStore((s) => s.tagHitCount)
  const skuByTag = useTagSkuScanStore((s) => s.skuByTag)
  const clear = useTagSkuScanStore((s) => s.clear)
  const removeTag = useTagSkuScanStore((s) => s.removeTag)
  const setSku = useTagSkuScanStore((s) => s.setSku)
  const applySkuToAll = useTagSkuScanStore((s) => s.applySkuToAll)

  const [search, setSearch] = useState('')
  const [bulkSku, setBulkSku] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<TagResult[]>([])
  const [errors, setErrors] = useState<EngineItemError[]>([])

  const errorLabels = useMemo<EngineErrorLabels>(
    () => ({
      alreadyTagged: t.Engine?.alreadyTagged,
      alreadyTaggedBirth: t.Engine?.alreadyTaggedBirth,
      alreadyTaggedTag: t.Engine?.alreadyTaggedTag,
      invalidEncoding: t.Engine?.invalidEncoding,
      notBirthed: t.Engine?.notBirthed,
      invalidSku: t.Engine?.invalidSku,
      birthPartial: t.Engine?.birthPartial,
      tagPartial: t.Engine?.tagPartial,
      allFailed: t.Engine?.allFailed,
      allOk: t.Engine?.allOk,
    }),
    [t]
  )

  const materialsQ = useQuery({
    queryKey: ['engine-material-master'],
    queryFn: () => rfidService.fetchMaterialDetails(''),
    staleTime: 5 * 60_000,
  })

  const materialOptions = useMemo(
    () => materialMasterDropdownOptions(materialsQ.data ?? []),
    [materialsQ.data]
  )

  const mock = isMockScannerEnabled()

  useEffect(() => {
    if (mock) ensureMockConnected()
  }, [mock])

  useEffect(() => {
    if (materialsQ.isError) {
      toast.error(
        t.Engine?.materialsLoadFailed ??
          ((materialsQ.error as { message?: string })?.message ||
            'Failed to load material master')
      )
    }
  }, [materialsQ.isError, materialsQ.error, t.Engine?.materialsLoadFailed])

  const sessionActive = isScannerSessionActive({ scannerConnected, scannerPending })
  const enabled = sessionActive || mock
  useTagSkuScanSubscription(enabled)

  const rows = useMemo<Row[]>(() => {
    return uniqueTags
      .filter((tagId) => {
        const sku = skuByTag[tagId] ?? ''
        const opt = materialOptions.find((o) => o.value === sku)
        return (
          engineSearchMatch(tagId, search) ||
          engineSearchMatch(sku, search) ||
          engineSearchMatch(opt?.description ?? '', search) ||
          engineSearchMatch(opt?.label ?? '', search)
        )
      })
      .map((tagId) => ({
        tagId,
        hits: tagHitCount[tagId] ?? 1,
        sku: skuByTag[tagId] ?? '',
      }))
  }, [uniqueTags, tagHitCount, skuByTag, search, materialOptions])

  const mappedCount = useMemo(
    () => uniqueTags.filter((id) => (skuByTag[id] ?? '').trim().length > 0).length,
    [uniqueTags, skuByTag]
  )

  const scannerLabel = deviceConnected?.rfid_device ?? scannerType ?? 'scanner'
  const materialCount = materialOptions.length
  const skuPlaceholder = t.Engine?.skuPlaceholder ?? 'Search material / SKU'
  const searchActive = search.trim().length > 0
  const matchLabel = searchActive
    ? (t.Engine?.searchShowing ?? 'Showing {{shown}} of {{total}}')
        .replace('{{shown}}', String(rows.length))
        .replace('{{total}}', String(uniqueTags.length))
    : undefined

  const onClear = () => {
    clear()
    setSearch('')
    setBulkSku('')
    setResults([])
    setErrors([])
    toast.message(t.Engine?.tagPoolCleared ?? 'Tag SKU pool cleared')
  }

  const onApplyBulk = (onlyEmpty: boolean) => {
    const sku = bulkSku.trim()
    if (!sku) {
      toast.error(t.Engine?.needBulkSku ?? 'Select a SKU to apply')
      return
    }
    if (uniqueTags.length === 0) {
      toast.error(t.Engine?.needScan ?? 'Scan at least one RFID tag')
      return
    }
    applySkuToAll(sku, onlyEmpty)
    toast.success(
      onlyEmpty
        ? t.Engine?.skuAppliedEmpty ?? 'SKU applied to empty rows'
        : t.Engine?.skuAppliedAll ?? 'SKU applied to all rows'
    )
  }

  const onMap = async () => {
    if (uniqueTags.length === 0) {
      toast.error(t.Engine?.needScan ?? 'Scan at least one RFID tag')
      return
    }

    const pairs = uniqueTags
      .map((rfid_code) => ({
        rfid_code,
        sku_code: (skuByTag[rfid_code] ?? '').trim(),
      }))
      .filter((p) => p.sku_code.length > 0)

    const missing = uniqueTags.length - pairs.length
    if (pairs.length === 0) {
      toast.error(t.Engine?.needSkuPerTag ?? 'Assign a SKU to each scanned tag')
      return
    }
    if (missing > 0) {
      toast.error(
        t.Engine?.incompleteSkuMap?.replace('{{count}}', String(missing)) ??
          `${missing} tag(s) still need a SKU`
      )
      return
    }

    setLoading(true)
    setResults([])
    setErrors([])
    try {
      if (pairs.length === 1) {
        const row = await engineService.tagOne(pairs[0].rfid_code, pairs[0].sku_code)
        setResults([row])
        clear()
        setSearch('')
        setBulkSku('')
        if (row.remapped) {
          toast.success(
            (t.Engine?.tagRemapped ?? 'Remapped from {{from}} → {{to}}')
              .replace('{{from}}', row.previous_sku_code || '—')
              .replace('{{to}}', row.sku_code)
          )
        } else if (row.created === false) {
          toast.success(t.Engine?.tagSameSku ?? 'Already mapped to this SKU')
        } else {
          toast.success(t.Engine?.tagOk ?? 'SKU mapped')
        }
      } else {
        const res = await engineService.tagMany(pairs)
        setResults(res.results)
        setErrors(res.errors)
        const failed = new Set(res.errors.map((e) => e.rfid_code.toUpperCase()))
        if (failed.size === 0) {
          clear()
          setSearch('')
          setBulkSku('')
        } else {
          for (const p of pairs) {
            if (!failed.has(p.rfid_code.toUpperCase())) removeTag(p.rfid_code)
          }
        }
        const remappedCount = res.results.filter((r) => r.remapped).length
        if (res.errors.length === 0 && remappedCount > 0) {
          toast.success(
            (t.Engine?.tagBatchRemapOk ?? '{{ok}} mapped ({{remapped}} remapped)')
              .replace('{{ok}}', String(res.results.length))
              .replace('{{remapped}}', String(remappedCount))
          )
        } else {
          const summary = summarizeEngineBatch('tag', res.results.length, res.errors, errorLabels)
          if (summary.tone === 'success') toast.success(summary.message)
          else if (summary.tone === 'warning') toast.warning(summary.message)
          else toast.error(summary.message)
        }
      }
    } catch (err) {
      const e = err as EngineApiError
      if (pairs[0] && (e.code === 'ALREADY_TAGGED' || e.code === 'NOT_BIRTHED')) {
        setErrors([
          {
            rfid_code: pairs[0].rfid_code,
            sku_code: pairs[0].sku_code,
            error: e.message || e.code || 'Tag failed',
            code: e.code || 'ERROR',
          },
        ])
      }
      toast.error(singleEngineErrorMessage(e, errorLabels))
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageContainer fluid className="!flex !h-full !min-h-0 !flex-col !px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title={t.Engine?.tagTitle ?? 'Tag SKU'}
          subtitle={t.Engine?.tagSubtitle ?? 'Scan tags, then marry each to a SKU'}
          onBack={() => navigate('/engine')}
          right={<ScanCaptureHint armed={enabled} showStatus={false} />}
        />

        <div className="rfid-wizard-body !flex !min-h-0 !flex-1 !flex-col !gap-2.5 !overflow-hidden">
          <div
            className="flex shrink-0 items-center rounded-2xl px-3 py-3.5"
            style={{ backgroundColor: SCAN_HERO_SOFT }}
          >
            <div className="flex flex-1 flex-col items-center">
              <div className="text-3xl font-black tabular-nums leading-none text-white">
                {rawScans}
              </div>
              <div className="mt-1 text-[10px] font-bold text-white/60">
                {t.Engine?.birthRawScans ?? 'Raw scans'}
              </div>
            </div>
            <div className="h-10 w-px bg-white/20" />
            <div className="flex flex-1 flex-col items-center">
              <div className="text-3xl font-black tabular-nums leading-none text-white">
                {uniqueTags.length}
              </div>
              <div className="mt-1 text-[10px] font-bold text-white/60">
                {t.Engine?.birthUniqueTags ?? 'Unique tags'}
              </div>
            </div>
            <div className="h-10 w-px bg-white/20" />
            <div className="flex flex-1 flex-col items-center">
              <div className="text-3xl font-black tabular-nums leading-none text-white">
                {mappedCount}
              </div>
              <div className="mt-1 text-[10px] font-bold text-white/60">
                {t.Engine?.tagMappedCount ?? 'With SKU'}
              </div>
            </div>
          </div>

          {!enabled ? (
            <button
              type="button"
              onClick={() => navigate('/scanner')}
              className="flex shrink-0 items-center justify-center gap-2.5 rounded-xl border-[1.5px] border-border bg-card px-3.5 py-3 text-[13px] font-bold text-foreground"
            >
              <Bluetooth className="h-[18px] w-[18px]" style={{ color: BRAND }} strokeWidth={2} />
              {t.Engine?.scannerNotConnected ?? 'Scanner not connected — tap to connect'}
            </button>
          ) : (
            <p className="m-0 shrink-0 text-center text-[11px] font-semibold leading-4 text-muted-foreground">
              {(
                t.Engine?.tagScanHint ??
                'Press the trigger on {{scanner}} to scan, then assign a SKU per tag.'
              ).replace('{{scanner}}', scannerLabel)}
            </p>
          )}

          {mock ? (
            <ManualTagEntry
              compact
              className="!mb-0 shrink-0"
              placeholder={t.Engine?.rfidCode ?? 'RFID code'}
            />
          ) : null}

          {uniqueTags.length > 0 ? (
            <div className="rfid-engine-bulk shrink-0 rounded-xl border border-border bg-card p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  {t.Engine?.bulkSku ?? 'Apply SKU'}
                </div>
                <div className="text-[10px] font-semibold text-muted-foreground">
                  {materialsQ.isLoading
                    ? t.Engine?.materialsLoading ?? 'Loading…'
                    : (t.Engine?.materialsCount ?? '{{count}} materials').replace(
                        '{{count}}',
                        String(materialCount)
                      )}
                </div>
              </div>
              <MaterialSkuSelect
                value={bulkSku}
                options={materialOptions}
                onChange={setBulkSku}
                placeholder={skuPlaceholder}
                loading={materialsQ.isLoading}
              />
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onApplyBulk(true)}
                  className="rounded-lg border border-border px-2.5 py-2.5 text-[12px] font-bold text-foreground"
                >
                  {t.Engine?.applyEmpty ?? 'Empty rows'}
                </button>
                <button
                  type="button"
                  onClick={() => onApplyBulk(false)}
                  className="rounded-lg px-2.5 py-2.5 text-[12px] font-bold text-white"
                  style={{ backgroundColor: BRAND }}
                >
                  {t.Engine?.applyAll ?? 'All rows'}
                </button>
              </div>
            </div>
          ) : null}

          <div className="flex shrink-0 items-center justify-between gap-2">
            <div className="text-[13px] font-black text-foreground">
              {t.Engine?.tagMapTable ?? 'Tag → SKU'}
              {uniqueTags.length > 0 ? (
                <span className="ml-1.5 text-[11px] font-bold text-muted-foreground">
                  ({uniqueTags.length})
                </span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClear}
              disabled={uniqueTags.length === 0 && results.length === 0 && errors.length === 0}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
              {t.Engine?.clear ?? 'Clear'}
            </button>
          </div>

          {uniqueTags.length > 0 ? (
            <EngineListSearch
              value={search}
              onChange={setSearch}
              placeholder={
                t.Engine?.searchTag ?? t.Engine?.searchScanned ?? 'Search tags or SKU…'
              }
              matchCount={rows.length}
              totalCount={uniqueTags.length}
              matchLabel={matchLabel}
              aria-label={t.Engine?.searchTag ?? 'Search tags or SKU'}
            />
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-[14px] border border-border bg-card">
            {uniqueTags.length === 0 ? (
              <p className="m-0 px-4 py-8 text-center text-xs font-semibold text-muted-foreground">
                {t.Engine?.birthEmpty ?? 'No tags yet — press the trigger to scan'}
              </p>
            ) : rows.length === 0 ? (
              <p className="m-0 px-4 py-8 text-center text-xs font-semibold text-muted-foreground">
                {t.Engine?.noSearchMatch ?? 'No tags match this search'}
              </p>
            ) : (
              <table className="rfid-engine-tag-table w-full border-collapse text-left">
                <thead className="sticky top-0 z-[1] bg-card">
                  <tr className="border-b border-border text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    <th className="w-[38%] px-3 py-2.5 font-bold">
                      {t.Engine?.rfidCode ?? 'RFID'}
                    </th>
                    <th className="px-2 py-2.5 font-bold">{t.Engine?.skuCode ?? 'SKU'}</th>
                    <th className="w-10 px-1 py-2.5" />
                    <th className="w-9 px-1 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const hasSku = row.sku.trim().length > 0
                    return (
                      <tr
                        key={row.tagId}
                        className={cn(
                          'rfid-engine-tag-row align-middle',
                          !hasSku && 'rfid-engine-tag-row--pending'
                        )}
                        style={{
                          borderTop: index === 0 ? undefined : '1px solid var(--border)',
                        }}
                      >
                        <td className="max-w-0 px-3 py-2 align-middle">
                          <span
                            className="block truncate font-mono text-[11.5px] font-extrabold text-foreground"
                            title={row.tagId}
                          >
                            {row.tagId}
                          </span>
                        </td>
                        <td className="min-w-[46%] px-2 py-1.5 align-middle">
                          <MaterialSkuSelect
                            compact
                            value={row.sku}
                            options={materialOptions}
                            onChange={(sku) => setSku(row.tagId, sku)}
                            placeholder={skuPlaceholder}
                            loading={materialsQ.isLoading}
                            size="small"
                          />
                        </td>
                        <td className="px-1 py-2 align-middle text-center">
                          {row.hits > 1 ? (
                            <span
                              className="inline-block rounded-lg px-1.5 py-0.5 text-[10px] font-black"
                              style={{ color: BRAND, backgroundColor: `${BRAND}18` }}
                            >
                              ×{row.hits}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-1 py-2 align-middle">
                          <button
                            type="button"
                            onClick={() => removeTag(row.tagId)}
                            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Remove ${row.tagId}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {results.length > 0 ? (
            <div className="max-h-20 shrink-0 space-y-1.5 overflow-y-auto">
              <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                {t.Engine?.results ?? 'Results'}
              </div>
              {results.map((r) => (
                <div
                  key={r.rfid_code}
                  className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs"
                >
                  <span className="break-all font-mono font-semibold">{r.rfid_code}</span>
                  <span className="ml-2 text-muted-foreground">
                    → {r.sku_code} · {r.status}
                    {r.remapped
                      ? ` · remapped from ${r.previous_sku_code || '—'}`
                      : r.created === false
                        ? ' · unchanged'
                        : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {errors.length > 0 ? (
            <div className="max-h-20 shrink-0 space-y-1.5 overflow-y-auto">
              <div className="text-[10px] font-bold uppercase tracking-wide text-red-600">
                {t.Engine?.errors ?? 'Errors'}
              </div>
              {errors.map((err) => (
                <div
                  key={`${err.rfid_code}-${err.code}`}
                  className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs"
                >
                  <span className="break-all font-mono font-semibold">{err.rfid_code}</span>
                  <span className="ml-2 text-red-700">
                    {engineItemErrorLabel(err, errorLabels)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          <div className="shrink-0 space-y-2">
            {!enabled ? (
              <AppButton block onClick={() => navigate('/scanner')}>
                {t.Engine?.connectScanner ?? 'Connect scanner'}
              </AppButton>
            ) : (
              <AppButton
                block
                loading={loading}
                disabled={uniqueTags.length === 0 || mappedCount < uniqueTags.length}
                onClick={onMap}
              >
                {t.Engine?.tagAction ?? 'Map SKU'}
                {uniqueTags.length > 0 ? ` (${mappedCount}/${uniqueTags.length})` : ''}
              </AppButton>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  )
}
